/**
 * Convenience hook that resolves to true when the current user has admin
 * privileges (i.e. `can_impersonate` on the backend — effectively `is_superuser`).
 *
 * Uses localStorage as an optimistic cache for instant renders, then
 * revalidates against the impersonation endpoint on mount.
 *
 * Note: this is a UX gate for "don't accidentally show this to field reps",
 * not a security boundary. A determined user can set the localStorage key.
 * Anything that actually needs to be secure must be enforced server-side.
 */

import { useEffect, useState } from 'react';
import { impersonationApi } from '../services/api';
import { useAuth } from './useAuth';

const CACHE_KEY = 'isAdmin';

export function useIsAdmin(): { isAdmin: boolean; isChecking: boolean } {
  const { user } = useAuth();
  const cached = typeof localStorage !== 'undefined'
    && localStorage.getItem(CACHE_KEY) === '1';
  const [isAdmin, setIsAdmin] = useState<boolean>(cached);
  const [isChecking, setIsChecking] = useState<boolean>(!cached);

  useEffect(() => {
    // Short-circuit non-superusers so we don't ping a superuser-only
    // endpoint (and pollute server logs with 403s) on every page load
    // (#1949). The check is authoritative because the backend stamps
    // is_superuser onto the user payload server-side.
    if (user && !user.is_superuser) {
      localStorage.removeItem(CACHE_KEY);
      setIsAdmin(false);
      setIsChecking(false);
      return;
    }
    // If we don't yet have a user (still bootstrapping auth), wait —
    // we'll re-run when `user` resolves rather than fire a guaranteed
    // 403 against the impersonate endpoint.
    if (!user) {
      return;
    }
    let mounted = true;
    impersonationApi
      .getList()
      .then((r) => {
        if (!mounted) return;
        const admin = !!r.can_impersonate;
        if (admin) localStorage.setItem(CACHE_KEY, '1');
        else localStorage.removeItem(CACHE_KEY);
        setIsAdmin(admin);
      })
      .catch(() => {
        if (!mounted) return;
        // 403 or network error — assume not admin
        localStorage.removeItem(CACHE_KEY);
        setIsAdmin(false);
      })
      .finally(() => {
        if (mounted) setIsChecking(false);
      });
    return () => {
      mounted = false;
    };
  }, [user]);

  return { isAdmin, isChecking };
}
